import React, { useEffect, useState } from 'react';
import { Download, FileText, Linkedin, Loader2, Mail, Phone, X } from 'lucide-react';
import { canDownloadPortalClientCv, downloadPortalClientCv } from '../../utils/portalClientCvDownload';
import { dispatchGlobalToast } from './RecruiterHeadsUpToast';
import type { ProposalRow } from '../../data/clientEngagementMock';
import { formatNlDateTime, proposalStatusLabel } from './engagementLabels';
import AiParsedBlocks from './AiParsedBlocks';

export interface EngagementCandidateModalProps {
  open: boolean;
  proposal: ProposalRow | null;
  onClose: () => void;
  /** Optioneel: ruwe `applications.cv_snapshot` (portal-document, extra velden). */
  cvSnapshot?: Record<string, unknown> | null;
}

const EngagementCandidateModal: React.FC<EngagementCandidateModalProps> = ({ open, proposal, onClose, cvSnapshot }) => {
  const [downloadBusy, setDownloadBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !proposal) return null;

  const snap = cvSnapshot && typeof cvSnapshot === 'object' && !Array.isArray(cvSnapshot) ? cvSnapshot : null;
  const formatted =
    snap?.portal_client_cv_formatted && typeof snap.portal_client_cv_formatted === 'object' && !Array.isArray(snap.portal_client_cv_formatted)
      ? (snap.portal_client_cv_formatted as Record<string, unknown>)
      : null;

  const hasCv =
    !!proposal.hasFormattedClientCv ||
    !!(proposal.cvPlainText && proposal.cvPlainText.trim()) ||
    !!(proposal.cvSummary && proposal.cvSummary.trim()) ||
    !!(proposal.aiParsed && Object.keys(proposal.aiParsed).length) ||
    !!(proposal.motivationOriginal && proposal.motivationOriginal.trim());

  const showDownload = canDownloadPortalClientCv(proposal);

  return (
    <div className="fixed inset-0 z-[210] flex justify-end" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]"
        aria-label="Sluit venster"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="eng-cand-title"
        className="relative flex h-full w-full max-w-md flex-col bg-white shadow-[-12px_0_40px_rgba(15,23,42,0.12)] sm:max-w-lg md:max-w-xl lg:max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4 md:px-6">
          <div className="flex min-w-0 gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-sm font-bold text-white">
              {proposal.initials}
            </div>
            <div className="min-w-0 pr-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Kandidaat</p>
              <h2 id="eng-cand-title" className="mt-0.5 text-lg font-semibold leading-snug text-slate-900 md:text-xl">
                {proposal.name}
              </h2>
              {proposal.role ? <p className="text-sm text-slate-500">{proposal.role}</p> : null}
              <p className="mt-1 text-xs text-slate-500">Vacature: {proposal.vacancyTitle}</p>
              <p className="mt-1 text-[11px] font-medium text-slate-500">
                {proposalStatusLabel(proposal.status)}
                {proposal.pipelineStatus ? ` · ${proposal.pipelineStatus}` : ''}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {showDownload ? (
              <button
                type="button"
                disabled={downloadBusy}
                onClick={async () => {
                  setDownloadBusy(true);
                  try {
                    await downloadPortalClientCv(proposal);
                    dispatchGlobalToast('CV wordt gedownload', 'success');
                  } catch (e) {
                    dispatchGlobalToast(e instanceof Error ? e.message : 'CV downloaden mislukt', 'error');
                  } finally {
                    setDownloadBusy(false);
                  }
                }}
                className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50 px-2.5 py-2 text-xs font-bold text-indigo-900 transition hover:bg-indigo-100 disabled:opacity-50"
              >
                {downloadBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                <span className="hidden sm:inline">Download CV</span>
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="Sluiten"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar">
          <div className="space-y-5 px-5 py-5 md:px-6 md:pb-8">
            {proposal.candidateEmail || proposal.candidatePhone || proposal.candidateLinkedin ? (
              <div className="flex flex-col gap-2 rounded-xl border border-slate-100 bg-slate-50/80 p-3 text-sm">
                {proposal.candidateEmail ? (
                  <a
                    href={`mailto:${proposal.candidateEmail}`}
                    className="flex items-center gap-2 font-medium text-indigo-700 hover:underline"
                  >
                    <Mail className="h-4 w-4 shrink-0" />
                    {proposal.candidateEmail}
                  </a>
                ) : null}
                {proposal.candidatePhone ? (
                  <a
                    href={`tel:${String(proposal.candidatePhone).replace(/\s/g, '')}`}
                    className="flex items-center gap-2 font-medium text-indigo-700 hover:underline"
                  >
                    <Phone className="h-4 w-4 shrink-0" />
                    {proposal.candidatePhone}
                  </a>
                ) : null}
                {proposal.candidateLinkedin ? (
                  <a
                    href={
                      String(proposal.candidateLinkedin).startsWith('http')
                        ? String(proposal.candidateLinkedin)
                        : `https://${String(proposal.candidateLinkedin).replace(/^\/+/, '')}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 font-medium text-indigo-700 hover:underline"
                  >
                    <Linkedin className="h-4 w-4 shrink-0" />
                    LinkedIn-profiel
                  </a>
                ) : null}
              </div>
            ) : null}

            <div className="grid gap-3 text-xs text-slate-500 sm:grid-cols-2">
              <div>
                <span className="font-bold uppercase tracking-wider text-slate-400">Ingediend</span>
                <p className="mt-0.5 font-medium text-slate-700">{formatNlDateTime(proposal.submittedAt)}</p>
              </div>
              <div>
                <span className="font-bold uppercase tracking-wider text-slate-400">Match</span>
                <p className="mt-0.5 font-medium text-slate-700">{proposal.matchScore}%</p>
              </div>
            </div>

            <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
              <p className="text-[11px] font-black uppercase tracking-wider text-indigo-900">Waarom dit een match is</p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                {(proposal.matchExplanation || '').trim() || 'Nog geen toelichting beschikbaar.'}
              </p>
            </div>

            <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-4">
              <p className="text-[11px] font-black uppercase tracking-wider text-slate-600">Feedback van de recruiter</p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                {(proposal.recruiterPortalSummary || '').trim() || 'Nog geen samenvatting van de recruiter.'}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {proposal.skills.map((s) => (
                <span key={s} className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                  {s}
                </span>
              ))}
            </div>

            {formatted ? (
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-900">Opgemaakt document voor klantportaal (recruiter)</p>
                <dl className="mt-2 space-y-1 text-sm text-slate-800">
                  {formatted.documentName != null ? (
                    <div>
                      <dt className="text-[10px] font-bold uppercase text-slate-500">Naam</dt>
                      <dd className="font-semibold">{String(formatted.documentName)}</dd>
                    </div>
                  ) : null}
                  {formatted.source != null ? (
                    <div>
                      <dt className="text-[10px] font-bold uppercase text-slate-500">Bron</dt>
                      <dd className="font-semibold">
                        {String(formatted.source) === 'documentbuilder' ? 'Documentbuilder' : String(formatted.source)}
                      </dd>
                    </div>
                  ) : null}
                  {formatted.attachedAt != null ? (
                    <div>
                      <dt className="text-[10px] font-bold uppercase text-slate-500">Gekoppeld op</dt>
                      <dd className="font-semibold">{formatNlDateTime(String(formatted.attachedAt))}</dd>
                    </div>
                  ) : null}
                </dl>
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                <p className="text-[11px] font-bold uppercase text-slate-400">Beschikbaarheid</p>
                <p className="mt-0.5 text-sm font-semibold text-slate-800">{proposal.availability}</p>
              </div>
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                <p className="text-[11px] font-bold uppercase text-slate-400">Salarisindicatie</p>
                <p className="mt-0.5 text-sm font-semibold text-slate-800">{proposal.salaryIndication}</p>
              </div>
            </div>

            {hasCv ? (
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
                <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-indigo-900">
                  <FileText className="h-4 w-4 shrink-0" />
                  Origineel profiel &amp; CV
                </p>
                {proposal.motivationOriginal?.trim() ? (
                  <div className="mb-4">
                    <p className="text-[11px] font-bold uppercase text-slate-500">Motivatie (origineel)</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{proposal.motivationOriginal.trim()}</p>
                  </div>
                ) : null}
                {proposal.cvPlainText?.trim() ? (
                  <div className="mb-4">
                    <p className="text-[11px] font-bold uppercase text-slate-500">Originele CV-tekst</p>
                    <pre className="mt-1 max-w-full whitespace-pre-wrap break-words rounded-lg bg-white/90 p-3 text-sm leading-relaxed text-slate-800 ring-1 ring-slate-100">
                      {proposal.cvPlainText}
                    </pre>
                  </div>
                ) : null}
                {proposal.cvSummary?.trim() ? (
                  <div className="mb-4">
                    <p className="text-[11px] font-bold uppercase text-slate-500">CV-samenvatting (systeem)</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{proposal.cvSummary}</p>
                  </div>
                ) : null}
                <AiParsedBlocks ai={proposal.aiParsed} />
              </div>
            ) : (
              <p className="text-sm text-slate-500">Geen CV- of profieldata beschikbaar.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementCandidateModal;
